import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Connect your Snowflake Account\n---\n\nOnce you've followed [the steps to create a new Snowflake user](/docs/snowflake-metadata-access) and signed up for SELECT using the invitation sent to your email, follow the steps below to connect your Snowflake account.\n\n## Step 1: Click \"Add Account\"\n\nClick the \"Add Account\" button on the home page, or navigate to Settings -> Snowflake -> Add New.\n\n## Step 2: Enter in your Snowflake account details\n\n{% blog-post-image imageName=\"enterSnowflakeAccountInfo\" alt=\"Enter Snowflake account info into SELECT\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20\" /%}\n\nHere are some more details on each field:\n\n- **Friendly Account Name:** You can put whatever you want here. If you only have one Snowflake account, then it’s common to just put your company name, for example: “Cyberco”. If you plan to connect multiple Snowflake account to SELECT, name them in a way you’ll be able to differentiate them using the account switcher on the top left. For example, you could have “Cyberco Prod” and “Cyberco Dev”.\n- **Account Identifier:** An account identifier uniquely identifies a Snowflake account within your organization. There are two ways to find it, [outlined below](/docs/connect-snowflake-account#finding-your-account-identifier).\n- **Username:** This will be the name of the new Snowflake user you created for SELECT. For example, `SELECT_USER`.\n- **Key-Pair:** The key pair you specified for the new user. Password authentication is supported but not recommended.\n- **[OPTIONAL] Role:** If you'd like to use a different role than the default one specified in the [Snowflake metadata access instructions](/docs/snowflake-metadata-access), you can specify it here.\n- **[OPTIONAL] Warehouse:** If you'd like to use a different warehouse than the default one specified in the [Snowflake metadata access instructions](/docs/snowflake-metadata-access), you can specify it here.\n- **Stage:** Enter the fully-qualified name of the stage you've created for SELECT's data access. Using a storage integration without a stage is also possible but not recommended, as many accounts do not allow exports without a stage.\n\n\nIf you encounter any difficulties, please contact support using the chat button on the bottom right and someone from our team will assist you right away.\n\n## Step 3: Hang tight!\n\nOnce the account has been successfully added, you’ll see a note on the home screen that your account’s initial sync is in progress. This usually takes 1-2 hours the first time it runs. You can reach out to the SELECT team to inquire about the sync status.\n\n\n## Finding your account identifier\n\n### Using Snowsight (Recommended)\n\nClick the dropdown menu button at the bottom left of the sidebar in the Snowsight UI. Click the copy account identifier button.\n\n{% blog-post-image imageName=\"findingAccountIdentifier\" alt=\"Snowflake account identifier\" imgClassName=\"rounded-md shadow-sm ring-2 ring-white/20\" /%}\n\n### Using the show accounts command\n\nUse the following SQL to fetch details about all Snowflake accounts in your organization:\n\n```sql\nuse role orgadmin;\nshow accounts;\n```\n\nHere’s an example output:\n\n| organization_name | account_name                  | snowflake_region |  account_url                                     |\n|-------------------|-------------------------------|------------------|-------------------------------------------------|\n| AKDOWIE           | EW94810          | GCP_US_EAST_4    |  https://akdowie-ew94810.snowflakecomputing.com          |\n| AKDOWIE           | DEV_AWS_US_EAST_1 | AWS_US_EAST_1    | https://akdowie-dev_aws_us_east_1.snowflakecomputing.com |\n\nYour account identifier is then: `<organization_name>.<account_name>`, i.e. `AKDOWIE.EW94810`.\n\nIf you have multiple accounts and aren't sure which is the desired account using the information in the table, run `select current_account()` in the desired account which will return the `account_locator` to help find the corresponding entry in the table (note the account locator is different to the account identifier).\n\n\nYou can learn more about account identifiers [here](https://docs.snowflake.com/en/user-guide/admin-account-identifier).\n";
const filepath = "/docs/connect-snowflake-account.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
